import React, { useState, useEffect, useCallback } from 'react';
import planejamentoService          from '../services/planejamento.service';
import planejamentoAnalistaService   from '../services/planejamentoAnalista.service';
import timelineTecnicaService        from '../services/timelineTecnica.service';
import colaboradorService            from '../services/colaborador.service';
import negocioService                from '../services/negocio.service';
import negocioAnexoService           from '../services/negocioAnexo.service';
import SelecionarItensModal          from './SelecionarItensModal';
import TimelineTecnica               from './TimelineTecnica';
import CallsModal                    from './CallsModal';
import AlterarEtapaModal             from './AlterarEtapaModal';
import SessaoCapacidade              from './SessaoCapacidade';

// ════════════════════════════════════════════════════════════════════════════
const COR = {
  azul:    '#003399', azulBg:  '#eff6ff',
  borda:   '#e8eaed', texto:   '#1a1a2e', muted: '#888',
  branco:  '#fff',    cinzaBg: '#fafafa',
  verde:   '#1D9E75', verdeBg: '#f0fff4',
  vermelho:'#e53935', vermBg:  '#fff0f0',
  amarelo: '#d97706', amlBg:   '#fffbeb',
  roxo:    '#7c3aed', roxoBg:  '#f3e8ff',
};

// Paleta de cores pra tags de analista — 8 cores com bom contraste entre si.
// Usadas com hash do nome do analista pra cor consistente em todas as
// aparições (mesma pessoa = mesma cor sempre).
const PALETA_ANALISTA = [
  { cor: '#0891b2', bg: '#cffafe' },  // ciano
  { cor: '#dc2626', bg: '#fee2e2' },  // vermelho
  { cor: '#7c3aed', bg: '#f3e8ff' },  // violeta
  { cor: '#059669', bg: '#d1fae5' },  // verde-escuro
  { cor: '#d97706', bg: '#fef3c7' },  // âmbar
  { cor: '#2563eb', bg: '#dbeafe' },  // azul
  { cor: '#db2777', bg: '#fce7f3' },  // rosa
  { cor: '#65a30d', bg: '#ecfccb' },  // verde-lima
];

// Hash determinístico simples do nome → índice na paleta.
// Mesmo analista = mesma cor sempre (não muda entre renders).
function corDoAnalista(nome) {
  if (!nome) return PALETA_ANALISTA[0];
  let hash = 0;
  for (let i = 0; i < nome.length; i++) {
    hash = ((hash << 5) - hash) + nome.charCodeAt(i);
    hash |= 0; // força int32
  }
  return PALETA_ANALISTA[Math.abs(hash) % PALETA_ANALISTA.length];
}

const TIPOS_LABEL = {
  'P': 'Projeto', 'U': 'Pontual', 'S': 'Service Desk', 'T': 'Sustentação',
};

const ETAPAS_TEC = {
  'Planejamento':      { cor: '#6b7280', bg: '#f3f4f6' },
  'Execução':          { cor: '#0ea5e9', bg: '#e0f2fe' },
  'Qualidade':         { cor: '#a855f7', bg: '#f3e8ff' },
  'Validação Cliente': { cor: '#f59e0b', bg: '#fef3c7' },
  'Acompanhamento':    { cor: '#06b6d4', bg: '#cffafe' },
  'Encerrado':         { cor: '#059669', bg: '#d1fae5' },
};

const inputStyle = {
  width: '100%', height: '34px', padding: '0 10px',
  border: `1px solid ${COR.borda}`, borderRadius: '5px',
  fontSize: '13px', boxSizing: 'border-box',
  backgroundColor: COR.branco, color: COR.texto, outline: 'none',
};
const inputDisabled = { ...inputStyle, backgroundColor: COR.cinzaBg, color: COR.muted };

// Formata número como BR: 12.5 → "12,50" · 0 → "0,00"
const formatHoras = (n) => {
  const v = parseFloat(n);
  if (isNaN(v)) return '0,00';
  return v.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

// Pct save (qtd/quantif × 100), retorna string formatada
const pctSave = (save, total) => {
  if (!total || total <= 0) return '—';
  return ((save / total) * 100).toFixed(1).replace('.', ',') + '%';
};

// ════════════════════════════════════════════════════════════════════════════
function PlanejamentoModal({ aberto, projetoId, usuario, onFechar }) {
  const [carregando, setCarregando] = useState(false);
  const [erro, setErro]             = useState('');
  const [dados, setDados]           = useState(null);

  // Estado das seções colapsáveis (padrão CRM): controla quais estão abertas.
  // Projeto, Horas e Timeline começam abertas; Analistas começa fechada.
  const [secoes, setSecoes] = useState({
    projeto:    true,
    horas:      true,
    analistas:  false,
    capacidade: true,
    timeline:   true,
  });
  const toggleSec = (k) => setSecoes(s => ({ ...s, [k]: !s[k] }));

  // Key incrementada quando algo muda nos analistas/itens — força recarregar
  // o resumo de capacidade (ex: ao adicionar analista, a sessão de Capacidade
  // recarrega via useEffect [refreshKey])
  const [capacidadeRefreshKey, setCapacidadeRefreshKey] = useState(0);
  const recarregarCapacidade = () => setCapacidadeRefreshKey(k => k + 1);

  // Estado dos inputs de Save por item:
  //   valor:  string formatada exibida no input
  //   erro:   mensagem (se Save > Quantif. ou outra validação)
  // Map<itemId, { valor, erro }>
  const [estadoSave, setEstadoSave] = useState({});

  // Salvar (botão manual)
  const [salvando, setSalvando]     = useState(false);
  const [msgSucesso, setMsgSucesso] = useState('');

  // ── ANALISTAS TÉCNICOS (sessão 3) ────────────────────────────────────────
  // Modelo pendente: lista local com flags _novo, _alterado e ids temporários
  // ('tmp_N') pra analistas novos. Removidos saem da exibição mas vão pra
  // removidosIds, processados ao clicar Salvar.
  const [analistas, setAnalistas]               = useState([]);
  const [colaboradores, setColaboradores]       = useState([]);
  const [carregandoAnalistas, setCarregandoAna] = useState(false);
  const [removidosIds, setRemovidosIds]         = useState([]); // IDs reais (existentes) marcados pra remoção
  const [tmpCounter, setTmpCounter]             = useState(0);  // contador pra ids temporários

  // Sub-modal de seleção de itens (abre ao adicionar/editar analista)
  const [itensModalAberto, setItensModalAberto] = useState(false);
  const [analistaEdicao, setAnalistaEdicao]     = useState(null); // null = novo

  // Form pra adicionar analista (qual colaborador escolher)
  const [colaboradorEscolhido, setColaboradorEscolhido] = useState('');

  // Toast de erro (validação de conflito etc)
  const [toastErro, setToastErro] = useState('');

  // ── TIMELINE TÉCNICA (sessão 4) ──────────────────────────────────────────
  const [eventosTimeline, setEventosTimeline] = useState([]);
  const [carregandoTL, setCarregandoTL]       = useState(false);

  // Sub-modais da Timeline Técnica
  const [callsModalAberto, setCallsModalAberto]       = useState(false);
  // Modal de Alterar Etapa Técnica (reusa AlterarEtapaModal com modoTecnico)
  const [alterarEtapaAberto, setAlterarEtapaAberto]   = useState(false);

  const carregar = useCallback(async () => {
    if (!projetoId) return;
    try {
      setCarregando(true);
      setErro(''); setMsgSucesso('');
      const d = await planejamentoService.obterDados(projetoId);
      // Snapshot do valor original de horas_save em cada item — usado pra
      // detectar quais itens têm alterações pendentes (Save mudou mas não foi
      // salvo ainda).
      d.itens = d.itens.map(i => ({
        ...i,
        _horas_save_original: parseFloat(i.horas_save) || 0,
      }));
      setDados(d);
      // Inicializa estado local com os valores atuais (já vindos do banco)
      const inicial = {};
      d.itens.forEach(i => {
        inicial[i.id] = { valor: formatHoras(i.horas_save), erro: '' };
      });
      setEstadoSave(inicial);
    } catch (err) {
      console.error('Erro ao carregar planejamento:', err);
      setErro(err.response?.data?.mensagem || 'Erro ao carregar dados do projeto.');
    } finally {
      setCarregando(false);
    }
  }, [projetoId]);

  // ── DOS016 — Documento de Horas x Atividades (por analista) ────────────────
  // Monta o documento em HTML (layout baseado no modelo_dos016.pdf) e abre em
  // nova aba pra impressão → "Salvar como PDF". Sem dependência de lib.
  const gerarDOS016 = (a) => {
    if (!dados) return;
    const proj = dados.projeto || {};
    const itensProjeto = dados.itens || [];

    // Junta os itens do analista com os dados completos do item de quantificação
    // (tópico + horas de execução, que não vêm na listagem de analistas).
    const linhas = (a.itens || []).map(ai => {
      const di = itensProjeto.find(x => String(x.id) === String(ai.item_quantificacao_id)) || {};
      return {
        topico:              di.topico || '',
        topico_descricao:    di.topico_descricao || '',
        subtopico:           ai.subtopico || di.subtopico || '',
        subtopico_descricao: ai.subtopico_descricao || di.subtopico_descricao || '',
        horas:               parseFloat(di.horas_execucao) || 0,
        tipo_descricao:      di.tipo_descricao || '',
        ordem:               di.ordem ?? 9999,
      };
    });

    // Agrupa por tópico (10.1, 10.2, 11.1…), preservando a ordem dos itens.
    const grupos = [];
    const idxPorTopico = {};
    linhas
      .sort((x, y) => x.ordem - y.ordem)
      .forEach(l => {
        const chave = l.topico || l.subtopico || '—';
        if (idxPorTopico[chave] === undefined) {
          idxPorTopico[chave] = grupos.length;
          grupos.push({ topico: l.topico, topico_descricao: l.topico_descricao, subitens: [], total: 0 });
        }
        const g = grupos[idxPorTopico[chave]];
        g.subitens.push(l);
        g.total += l.horas;
      });

    const totalGeral   = grupos.reduce((s, g) => s + g.total, 0);
    const tipos        = [...new Set(linhas.map(l => l.tipo_descricao).filter(Boolean))];
    const tipoAtividade= tipos.join(', ') || '—';
    const analistaNome = a.nome_amigavel || a.nome || '—';
    const hoje         = new Date().toLocaleDateString('pt-BR');
    const versao       = dados.versao_atual ? String(dados.versao_atual).padStart(3, '0') : '—';
    const projetoLabel = `${proj.id || ''}${proj.descricao ? ' – ' + proj.descricao : ''}`;
    const clienteNome  = proj.cliente_nome || proj.cliente_razao_social || '—';
    const fmtCnpj = (v) => {
      const d = String(v || '').replace(/\D/g, '');
      return d.length === 14
        ? d.replace(/^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/, '$1.$2.$3/$4-$5')
        : (v || '—');
    };
    const cnpj         = fmtCnpj(proj.cliente_cnpj);
    const logoUrl      = `${window.location.origin}/alpha-logo.svg`;

    const esc = (s) => String(s ?? '').replace(/[&<>"]/g, c => (
      { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]));

    const gruposHtml = grupos.map(g => `
      <tr>
        <td class="ativ">
          <div class="grp">
            ${g.topico ? `<span class="badge">${esc(g.topico)}</span>` : ''}
            <span class="grp-titulo">${esc(g.topico_descricao || g.topico || '—')}</span>
          </div>
          <div class="subs">
            ${g.subitens.map(s => `<div class="sub">${esc(s.subtopico)}${s.subtopico_descricao ? ' - ' + esc(s.subtopico_descricao) : ''}</div>`).join('')}
          </div>
        </td>
        <td class="tot">${esc(formatHoras(g.total))}</td>
      </tr>`).join('');

    // Ícones das redes sociais — traço/preenchimento branco (sobre a faixa azul)
    const ICON_FB = '<svg width="12" height="12" viewBox="0 0 24 24"><rect width="24" height="24" rx="5" fill="#fff"/><path fill="#003399" d="M13.5 20v-6.2h2.05l.3-2.4h-2.35V9.85c0-.7.2-1.17 1.2-1.17h1.28V6.53c-.22-.03-.98-.1-1.87-.1-1.85 0-3.12 1.13-3.12 3.2v1.77H9v2.4h1.97V20h2.53z"/></svg>';
    const ICON_IG = '<svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#fff" stroke-width="2"><rect x="3" y="3" width="18" height="18" rx="5"/><circle cx="12" cy="12" r="4.2"/><circle cx="17.2" cy="6.8" r="1.1" fill="#fff" stroke="none"/></svg>';
    const ICON_IN = '<svg width="12" height="12" viewBox="0 0 24 24" fill="#fff"><path d="M4.98 3.5A2.5 2.5 0 1 0 5 8.5a2.5 2.5 0 0 0-.02-5zM3.2 9h3.6v11.5H3.2zM9.3 9h3.45v1.57h.05c.48-.9 1.65-1.85 3.4-1.85 3.64 0 4.3 2.4 4.3 5.5v6.28h-3.6v-5.56c0-1.33-.02-3.03-1.85-3.03-1.85 0-2.13 1.44-2.13 2.93v5.66H9.3z"/></svg>';

    const html = `<!doctype html>
<html lang="pt-BR"><head><meta charset="utf-8">
<title>DOS016 - ${esc(analistaNome)} - ${esc(proj.id || '')}</title>
<link rel="preconnect" href="https://fonts.googleapis.com">
<link rel="preconnect" href="https://fonts.gstatic.com" crossorigin>
<link href="https://fonts.googleapis.com/css2?family=Manrope:wght@400;500;600;700;800&display=swap" rel="stylesheet">
<style>
  @page { size: A4; margin: 0; }
  * { box-sizing: border-box; }
  html, body { margin: 0; padding: 0; }
  body { font-family: 'Manrope', 'Segoe UI', Arial, Helvetica, sans-serif; color: #1a1a2e;
         -webkit-print-color-adjust: exact; print-color-adjust: exact; }

  /* Faixa azul do topo (full-bleed) — logo estilo sidebar + redes sociais brancas */
  .header-band { background: #003399; display: flex; justify-content: space-between;
                 align-items: center; padding: 7px 22mm 7px 12mm; }
  .header-band .logo { height: 38px; width: auto; display: block; }
  .social { display: flex; flex-direction: column; gap: 3px; align-items: flex-start; }
  .social .item { display: flex; align-items: center; gap: 6px; color: #fff; font-size: 10.5px; font-weight: 600; }
  .social .item svg { display: block; }

  /* Miolo (dentro das margens laterais) */
  .content { padding: 68px 22mm 96px; }

  .titulo-bar { display: flex; align-items: stretch; border: 1.5px solid #003399;
                border-radius: 8px; overflow: hidden; margin-bottom: 56px; }
  .dos { background: #003399; color: #fff; font-weight: 800; font-size: 21px; letter-spacing: .5px;
         display: flex; align-items: center; padding: 6px 18px; }
  .dos-desc { font-size: 17px; font-weight: 700; display: flex; align-items: center; padding: 6px 16px; }

  .inforow { display: flex; border: 1.5px solid #003399; border-radius: 5px; overflow: hidden; margin-bottom: 8px; }
  .cell { padding: 3px 12px; border-right: 1px solid #cdd8ef; min-width: 0; }
  .cell:last-child { border-right: none; }
  .grow { flex: 1; }
  .cell.col1 { flex: 0 0 64%; }
  .cell.colv { flex: 0 0 84px; }
  .lbl { font-size: 9.5px; font-weight: 700; color: #003399; line-height: 1.15; }
  .val { font-size: 13px; font-weight: 700; margin-top: 0; line-height: 1.2; word-break: break-word; }

  .inforow3 { display: flex; gap: 10px; margin-bottom: 64px; }
  .ibox { border: 1.5px solid #003399; border-radius: 5px; padding: 3px 12px; min-width: 0; }
  .ibox.grow { flex: 2 1 0; }
  .ibox.wide { flex: 1 1 0; }
  .ibox.horas { background: #003399; text-align: center; min-width: 128px; }
  .ibox.horas .lbl { color: #cfe0ff; }
  .ibox.horas .val { color: #fff; font-size: 16px; }

  .tabela-wrap { border-radius: 8px 8px 0 0; overflow: hidden; }
  table.ativ-table { width: 100%; border-collapse: collapse; border-bottom: 2.5px solid #003399; }
  th.th-ativ, th.th-tot { background: #003399; color: #fff; font-size: 13px; font-weight: 700; padding: 5px 14px; }
  th.th-ativ { text-align: center; }
  th.th-tot { text-align: center; width: 110px; }
  td.ativ { padding: 10px 14px; border-bottom: 1px solid #e2e6ee; vertical-align: top; }
  td.tot { padding: 10px 14px; border-bottom: 1px solid #e2e6ee; border-left: 1px solid #e2e6ee;
           text-align: center; font-weight: 800; color: #003399; font-size: 14px;
           vertical-align: middle; white-space: nowrap; }
  tbody tr:nth-child(even) td { background: #f0f2f7; }
  .grp { display: flex; align-items: center; gap: 8px; margin-bottom: 5px; }
  .badge { background: #003399; color: #fff; font-size: 10px; font-weight: 700; padding: 2px 7px;
           border-radius: 4px; letter-spacing: .3px; }
  .grp-titulo { font-size: 14px; font-weight: 700; color: #003399; }
  .subs { padding-left: 26px; }
  .sub { font-size: 10.5px; color: #555; line-height: 1.6; }

  /* Faixa azul do rodapé (full-bleed, fixa no pé) */
  .footer-band { position: fixed; left: 0; right: 0; bottom: 0; background: #003399; color: #fff;
                 display: flex; justify-content: space-between; align-items: center;
                 font-size: 9.5px; font-weight: 700; padding: 10px 22mm; }
  .footer-band a { color: #fff; text-decoration: none; }

  .toolbar { position: fixed; top: 12px; right: 12px; }
  .toolbar button { background: #1D9E75; color: #fff; border: none; border-radius: 6px;
                    padding: 9px 16px; font-size: 13px; font-weight: 700; cursor: pointer;
                    box-shadow: 0 2px 8px rgba(0,0,0,.2); }
  @media print { .toolbar { display: none; } }
</style></head>
<body>
  <div class="toolbar"><button onclick="window.print()">🖨️ Imprimir / Salvar PDF</button></div>

  <div class="header-band">
    <img class="logo" src="${logoUrl}" alt="Alpha ERP" onerror="this.style.display='none'">
    <div class="social">
      <div class="item">${ICON_FB}<span>/alphaerpbr</span></div>
      <div class="item">${ICON_IG}<span>/alphaerpbr</span></div>
      <div class="item">${ICON_IN}<span>/alpha-erp</span></div>
    </div>
  </div>

  <div class="content">
    <div class="titulo-bar">
      <span class="dos">DOS016</span>
      <span class="dos-desc">Documento de Horas de Execução</span>
    </div>

    <div class="inforow">
      <div class="cell col1"><div class="lbl">Projeto</div><div class="val">${esc(projetoLabel)}</div></div>
      <div class="cell grow"><div class="lbl">Data</div><div class="val">${esc(hoje)}</div></div>
      <div class="cell colv"><div class="lbl">Versão</div><div class="val">${esc(versao)}</div></div>
    </div>
    <div class="inforow">
      <div class="cell col1"><div class="lbl">Cliente</div><div class="val">${esc(clienteNome)}</div></div>
      <div class="cell grow"><div class="lbl">Cnpj</div><div class="val">${esc(cnpj)}</div></div>
    </div>

    <div class="inforow3">
      <div class="ibox wide"><div class="lbl">Tipo de Atividade</div><div class="val">${esc(tipoAtividade)}</div></div>
      <div class="ibox grow"><div class="lbl">Analista</div><div class="val">${esc(analistaNome)}</div></div>
      <div class="ibox horas"><div class="lbl">Horas Totais</div><div class="val">${esc(formatHoras(totalGeral))}</div></div>
    </div>

    <div class="tabela-wrap">
      <table class="ativ-table">
        <thead><tr><th class="th-ativ">Atividades</th><th class="th-tot">Total</th></tr></thead>
        <tbody>${gruposHtml || '<tr><td class="ativ">Nenhuma atividade atribuída a este analista.</td><td class="tot">0,00</td></tr>'}</tbody>
      </table>
    </div>
  </div>

  <div class="footer-band">
    <span>DOS0XX – Documento de Horas x Atividades</span>
    <a href="https://www.alphaerp.com.br">www.alphaerp.com.br</a>
  </div>

  <script>
    function imprimirDoc(){ setTimeout(function(){ window.print(); }, 150); }
    if (document.fonts && document.fonts.ready) { document.fonts.ready.then(imprimirDoc); }
    else { window.onload = imprimirDoc; }
  </script>
</body></html>`;

    const win = window.open('', '_blank');
    if (!win) { setToastErro('Permita pop-ups para gerar o DOS016.'); return; }
    win.document.write(html);
    win.document.close();
    win.focus();
  };

  useEffect(() => {
    if (aberto) {
      carregar();
      carregarAnalistas();
      carregarColaboradores();
      carregarTimeline();
    }
  }, [aberto, carregar]);

  // ── Carregar lista de analistas atuais do projeto ──
  const carregarAnalistas = useCallback(async () => {
    if (!projetoId) return;
    try {
      setCarregandoAna(true);
      const lista = await planejamentoAnalistaService.listar(projetoId);
      setAnalistas(lista);
      setRemovidosIds([]);
      setTmpCounter(0);
    } catch (err) {
      console.error('Erro ao carregar analistas:', err);
      setAnalistas([]);
    } finally {
      setCarregandoAna(false);
    }
  }, [projetoId]);

  // ── Carregar colaboradores ativos (pra dropdown) ──
  const carregarColaboradores = useCallback(async () => {
    try {
      const lista = await colaboradorService.listar();
      setColaboradores(lista.filter(c => c.status === 'A'));
    } catch (err) {
      console.error('Erro ao carregar colaboradores:', err);
      setColaboradores([]);
    }
  }, []);

  // ── Carregar timeline técnica ──
  const carregarTimeline = useCallback(async () => {
    if (!projetoId) return;
    try {
      setCarregandoTL(true);
      const lista = await timelineTecnicaService.listar(projetoId);
      setEventosTimeline(lista);
    } catch (err) {
      console.error('Erro ao carregar timeline técnica:', err);
      setEventosTimeline([]);
    } finally {
      setCarregandoTL(false);
    }
  }, [projetoId]);

  // ── Após criar uma call no CallsModal, se a etapa técnica for "Planejamento",
  //    pergunta se quer avançar pra "Execução" ────────────────────────────────
  const handleCallCriada = async () => {
    // Pega a etapa técnica atual do projeto carregado
    const etapaAtual = projeto?.etapa_tecnica;
    if (etapaAtual !== 'Planejamento') return;

    if (window.confirm('Call agendada! Deseja avançar a etapa técnica para "Execução"?')) {
      try {
        await negocioService.alterarEtapaTecnica(projetoId, {
          nova_etapa_tecnica: 'Execução',
          motivo:             'Avanço automático após agendamento de call',
          data_evento:        new Date().toISOString().slice(0, 10),
          usuario_id:         usuario?.id || null,
        });
        await carregar();
        await carregarTimeline();
      } catch (err) {
        alert(err.response?.data?.mensagem || 'Erro ao avançar etapa técnica.');
      }
    }
  };

  // ── Fecha o CallsModal e recarrega a timeline ──
  const handleFecharCallsModal = async () => {
    setCallsModalAberto(false);
    await carregarTimeline();
  };

  // ── Sucesso do AlterarEtapaModal (etapa técnica alterada) ──
  const handleSucessoAlterarEtapa = async () => {
    setAlterarEtapaAberto(false);
    await carregar();
    await carregarTimeline();
  };

  if (!aberto) return null;

  // ══ Handlers ═══════════════════════════════════════════════════════════
  // Helper: parse BR ("12,5") → número
  const parseBR = (str) => {
    const num = parseFloat(String(str).replace(/\./g, '').replace(',', '.'));
    return isNaN(num) ? 0 : num;
  };

  // Quando o usuário digita no input de Save:
  //   • atualiza o estado local
  //   • valida (Save <= Quantif.)
  //   • atualiza horas_execucao do item EM MEMÓRIA (preview, sem salvar)
  //   • recalcula totais por tipo + total geral também em memória
  const handleSaveChange = (item, valorRaw) => {
    const valor = String(valorRaw).replace(/[^\d,.]/g, '');
    const num = parseBR(valor);
    const horasTotal = parseFloat(item.horas) || 0;

    // Validação
    let erro = '';
    if (num > horasTotal) {
      erro = `Save não pode ser maior que ${formatHoras(horasTotal)}h`;
    } else if (num < 0) {
      erro = 'Save não pode ser negativo';
    }

    setEstadoSave(s => ({ ...s, [item.id]: { valor, erro } }));

    // Atualiza preview do item + totais (mesmo se inválido — pra UI ficar viva)
    setDados(d => {
      if (!d) return d;
      const novosItens = d.itens.map(i => {
        if (i.id !== item.id) return i;
        const novoSave = erro ? parseFloat(i.horas_save) || 0 : num;
        const novaExec = novoSave === 0
          ? horasTotal
          : Math.max(0, horasTotal - novoSave);
        return {
          ...i,
          // só sobrescreve a memória se válido (assim cards refletem o valor digitado)
          horas_save:     erro ? i.horas_save     : Number(novoSave.toFixed(2)),
          horas_execucao: erro ? i.horas_execucao : Number(novaExec.toFixed(2)),
        };
      });
      return { ...d, ...recalcularTotais(novosItens), itens: novosItens };
    });
  };

  // Salva todas as alterações pendentes ao clicar em "💾 Salvar".
  // Inclui horas (Save) + criação/edição/remoção de analistas.
  const salvarTudo = async () => {
    setErro(''); setMsgSucesso('');

    // Bloqueia se houver erro em algum input
    const itensComErro = Object.entries(estadoSave).filter(([_, est]) => est.erro);
    if (itensComErro.length > 0) {
      return setErro(`Existem ${itensComErro.length} item(ns) com Save inválido. Corrija antes de salvar.`);
    }

    // 1) Pendências de horas Save
    const pendentesHoras = [];
    dados.itens.forEach(item => {
      const est = estadoSave[item.id];
      if (!est) return;
      const novoValor = parseBR(est.valor);
      const valorOriginal = parseFloat(item._horas_save_original ?? item.horas_save) || 0;
      if (Math.abs(novoValor - valorOriginal) > 0.001) {
        pendentesHoras.push({ item, novoValor });
      }
    });

    // 2) Pendências de analistas
    const novosAnalistas    = analistas.filter(a => a._novo);
    const editadosAnalistas = analistas.filter(a => !a._novo && a._alterado);
    const removidosAnalistas = removidosIds;

    const totalPendencias = pendentesHoras.length
                          + novosAnalistas.length
                          + editadosAnalistas.length
                          + removidosAnalistas.length;

    if (totalPendencias === 0) {
      setMsgSucesso('Nenhuma alteração para salvar.');
      setTimeout(() => setMsgSucesso(''), 2000);
      return;
    }

    setSalvando(true);
    try {
      // 1) Horas
      for (const { item, novoValor } of pendentesHoras) {
        await planejamentoService.atualizarHorasSave(item.id, novoValor);
      }

      // 2) Removidos primeiro (libera itens pra possíveis novos)
      for (const remId of removidosAnalistas) {
        await planejamentoAnalistaService.excluir(remId);
      }

      // 3) Edições de existentes
      for (const a of editadosAnalistas) {
        const ids = (a.itens || []).map(i => i.item_quantificacao_id);
        await planejamentoAnalistaService.atualizarItens(a.id, ids);
      }

      // 4) Novos
      for (const a of novosAnalistas) {
        const ids = (a.itens || []).map(i => i.item_quantificacao_id);
        await planejamentoAnalistaService.criar(projetoId, {
          colaborador_id: a.colaborador_id,
          item_ids: ids,
          usuario_id: usuario?.id || null,
        });
      }

      // Recarrega tudo do banco pra garantir consistência
      await carregar();
      await carregarAnalistas();
      await carregarTimeline();
      // Recarrega a sessão de Capacidade (analistas adicionados/removidos
      // ou com itens alterados afetam horas previstas)
      recarregarCapacidade();
      setMsgSucesso(`✓ ${totalPendencias} alteração(ões) salvas com sucesso.`);
      setTimeout(() => setMsgSucesso(''), 3000);
    } catch (err) {
      setErro(err.response?.data?.mensagem || 'Erro ao salvar alterações.');
    } finally {
      setSalvando(false);
    }
  };

  // Verifica se há pendências de horas (pra indicador "alterações não salvas")
  const temPendenciasHoras = dados?.itens?.some(item => {
    const est = estadoSave[item.id];
    if (!est) return false;
    const novoValor = parseBR(est.valor);
    const valorOriginal = parseFloat(item._horas_save_original ?? item.horas_save) || 0;
    return Math.abs(novoValor - valorOriginal) > 0.001;
  });

  // Recalcula totais por tipo + total geral a partir da lista de itens
  const recalcularTotais = (itens) => {
    const mapa = {};
    itens.forEach(item => {
      const key = item.tipo_atividade_id || '_sem_tipo';
      if (!mapa[key]) {
        mapa[key] = {
          tipo_atividade_id: item.tipo_atividade_id,
          tipo_descricao:    item.tipo_descricao || 'Sem tipo',
          tipo_cor:          item.tipo_cor       || '#888',
          tipo_sigla:        item.tipo_sigla     || '',
          horas_quantificadas: 0,
          horas_save:          0,
          horas_execucao:      0,
        };
      }
      mapa[key].horas_quantificadas += parseFloat(item.horas)          || 0;
      mapa[key].horas_save          += parseFloat(item.horas_save)     || 0;
      mapa[key].horas_execucao      += parseFloat(item.horas_execucao) || 0;
    });
    const totais_por_tipo = Object.values(mapa);
    const total_geral = {
      horas_quantificadas: totais_por_tipo.reduce((s, t) => s + t.horas_quantificadas, 0),
      horas_save:          totais_por_tipo.reduce((s, t) => s + t.horas_save,          0),
      horas_execucao:      totais_por_tipo.reduce((s, t) => s + t.horas_execucao,      0),
    };
    return { totais_por_tipo, total_geral };
  };

  // ══ Handlers Analistas (modo PENDENTE) ════════════════════════════════
  // Mudanças ficam em memória até clicar "Salvar". Toast de erro mostra
  // conflitos de validação imediatamente.

  // Mostra toast de erro temporário (3s)
  const mostrarErro = (msg) => {
    setToastErro(msg);
    setTimeout(() => setToastErro(''), 3000);
  };

  // Mapa de itens já alocados (entre analistas visíveis na tela)
  // Map<itemId, { analistaId, analistaNome }>
  const atribuicoesPorItem = {};
  analistas.forEach(a => {
    (a.itens || []).forEach(item => {
      atribuicoesPorItem[item.item_quantificacao_id] = {
        analistaId: a.id,
        analistaNome: a.nome_amigavel || a.nome,
      };
    });
  });

  // Adiciona novo analista (em memória)
  const adicionarAnalista = () => {
    if (!colaboradorEscolhido) return;
    if (analistas.some(a => a.colaborador_id === colaboradorEscolhido)) {
      mostrarErro('Este colaborador já é analista deste projeto.');
      return;
    }
    // Acha dados do colaborador
    const col = colaboradores.find(c => c.id === colaboradorEscolhido);
    if (!col) return;

    // Abre sub-modal pra escolher itens. Cria id temporário.
    const tmpId = `tmp_${tmpCounter + 1}`;
    setTmpCounter(c => c + 1);
    setAnalistaEdicao({
      _novo: true,
      id: tmpId,
      colaborador_id: col.id,
      nome: col.nome,
      nome_amigavel: col.usuario_nome || col.nome,
      itens: [],
    });
    setItensModalAberto(true);
  };

  const editarItensAnalista = (analista) => {
    setAnalistaEdicao(analista);
    setItensModalAberto(true);
  };

  // Remove analista da lista. Se for existente (id numérico), vai pra fila
  // de remoção no backend ao salvar. Se for novo (id 'tmp_N'), só descarta.
  const removerAnalista = (analista) => {
    const nome = analista.nome_amigavel || analista.nome;
    const qtdItens = (analista.itens || []).length;
    const msg = qtdItens > 0
      ? `Remover ${nome}? ${qtdItens} item(ns) ficarão sem analista atribuído.\n\nA remoção só será efetivada ao clicar Salvar.`
      : `Remover ${nome}?\n\nA remoção só será efetivada ao clicar Salvar.`;
    if (!window.confirm(msg)) return;

    // Tira da lista de exibição
    setAnalistas(prev => prev.filter(a => a.id !== analista.id));

    // Se era um analista existente (id numérico), enfileira pra remoção
    if (typeof analista.id === 'number' || /^\d+$/.test(String(analista.id))) {
      setRemovidosIds(prev => [...prev, analista.id]);
    }
  };

  // Confirmação do sub-modal de seleção de itens (cria/edita em memória)
  const handleConfirmarItens = (idsArray) => {
    setItensModalAberto(false);
    if (!analistaEdicao) return;

    // Monta os objetos de item (subtopico, descricao, horas) a partir da
    // lista de itens da quantificação
    const itensQuantif = dados?.itens || [];
    const novosItens = idsArray.map(itemId => {
      const it = itensQuantif.find(q => q.id === itemId);
      return {
        atribuicao_id: `tmp_atrib_${itemId}`, // só pra key
        analista_id: analistaEdicao.id,
        item_quantificacao_id: itemId,
        subtopico: it?.subtopico || '',
        subtopico_descricao: it?.subtopico_descricao || '',
        horas: it?.horas || 0,
      };
    });

    if (analistaEdicao._novo) {
      // Adiciona novo na lista
      setAnalistas(prev => [...prev, { ...analistaEdicao, itens: novosItens }]);
      setColaboradorEscolhido('');
    } else {
      // Atualiza itens do existente
      setAnalistas(prev => prev.map(a =>
        a.id === analistaEdicao.id
          ? { ...a, itens: novosItens, _alterado: true }
          : a
      ));
    }
    setAnalistaEdicao(null);
  };

  // Existe alguma alteração pendente em analistas?
  const temPendenciasAnalistas =
    removidosIds.length > 0 ||
    analistas.some(a => a._novo || a._alterado);

  // Pendências totais (horas OU analistas) — controla habilitação do Salvar
  const temPendencias = temPendenciasHoras || temPendenciasAnalistas;

  // Fechar com confirmação se houver alterações não salvas
  const handleFechar = () => {
    if (temPendencias) {
      if (!window.confirm('Há alterações não salvas. Deseja descartá-las e fechar?')) return;
    }
    onFechar();
  };

  // ══ Render ═════════════════════════════════════════════════════════════
  const projeto = dados?.projeto;
  const itens   = dados?.itens || [];
  const totaisPorTipo = dados?.totais_por_tipo || [];
  const totalGeral    = dados?.total_geral || { horas_quantificadas: 0, horas_save: 0, horas_execucao: 0 };

  const etapaTecCor = projeto?.etapa_tecnica
    ? ETAPAS_TEC[projeto.etapa_tecnica] || { cor: '#6b7280', bg: '#f3f4f6' }
    : { cor: '#6b7280', bg: '#f3f4f6' };

  return (
    <>
      {/* Overlay */}
      {/* r133: overlay sem onClick — fechar so pelo Cancelar/x ou Salvar */}
      <div style={{
        position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
        backgroundColor: 'rgba(0,0,0,0.4)', zIndex: 9999,
      }} />

      {/* Modal */}
      <div style={{
        position: 'fixed', top: '50%', left: '50%',
        transform: 'translate(-50%, -50%)',
        width: '95%', maxWidth: '1100px', maxHeight: '92vh',
        backgroundColor: COR.branco, borderRadius: '8px',
        boxShadow: '0 10px 40px rgba(0,0,0,0.2)',
        display: 'flex', flexDirection: 'column',
        zIndex: 10000, overflow: 'hidden',
        fontFamily: "'Manrope', sans-serif",
      }}>
        {/* Header */}
        <div style={{
          padding: '14px 20px', borderBottom: `1px solid ${COR.borda}`,
          display: 'flex', alignItems: 'center', gap: '12px',
          backgroundColor: COR.azul, color: COR.branco,
        }}>
          <span style={{ fontSize: '18px' }}>🛠️</span>
          <span style={{ fontSize: '15px', fontWeight: '600', flex: 1 }}>
            Planejamento de Projeto
          </span>
          <button onClick={handleFechar} style={{
            border: 'none', background: 'transparent', color: COR.branco,
            fontSize: '20px', cursor: 'pointer',
          }}>×</button>
        </div>

        {/* Toast de erro (validações imediatas — conflitos de itens etc) */}
        {toastErro && (
          <div style={{
            padding: '10px 20px', backgroundColor: '#fee2e2',
            color: COR.vermelho, fontSize: '12px', fontWeight: '600',
            borderBottom: `1px solid ${COR.vermelho}33`,
            display: 'flex', alignItems: 'center', gap: '8px',
          }}>
            <span>⚠️</span>
            <span style={{ flex: 1 }}>{toastErro}</span>
            <button onClick={() => setToastErro('')} style={{
              border: 'none', background: 'transparent', color: COR.vermelho,
              fontSize: '16px', cursor: 'pointer', lineHeight: 1,
            }}>×</button>
          </div>
        )}

        {/* Body scrollável */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '16px 20px' }}>

          {carregando ? (
            <div style={{ padding: '40px', textAlign: 'center', color: COR.muted }}>
              Carregando...
            </div>
          ) : erro ? (
            <div style={{
              padding: '20px', backgroundColor: COR.vermBg,
              border: '1px solid #ffcccc', color: COR.vermelho,
              borderRadius: '6px', fontSize: '13px',
            }}>{erro}</div>
          ) : (
            <>
              {/* ═══════ Sessão 1 — Projeto ═══════ */}
              <Secao titulo="Projeto" icone="📋"
                aberta={secoes.projeto}
                onToggle={() => toggleSec('projeto')}>
                {/* Linha 1: Código (compacto) + Descrição (flex) + Etapa + Botão Alterar */}
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: '110px 1fr 200px',
                  gap: '12px',
                  marginBottom: '12px',
                }}>
                  <Campo label="Código">
                    <input value={projeto?.id || '—'} disabled style={inputDisabled} />
                  </Campo>
                  <Campo label="Descrição">
                    <input value={projeto?.descricao || '—'} disabled style={inputDisabled} />
                  </Campo>
                  <Campo label="Etapa">
                    <div style={{
                      display: 'flex', gap: '6px', alignItems: 'center',
                    }}>
                      <div style={{
                        flex: 1,
                        height: '34px', padding: '0 10px',
                        border: `1px solid ${etapaTecCor.cor}`,
                        backgroundColor: etapaTecCor.bg,
                        color: etapaTecCor.cor,
                        fontSize: '13px', fontWeight: '600',
                        borderRadius: '5px',
                        display: 'flex', alignItems: 'center',
                      }}>
                        {projeto?.etapa_tecnica || '—'}
                      </div>
                      {/* Botão só aparece quando estamos em Planejamento ou Execução
                          (etapas onde a alteração via Planejamento é permitida).
                          Outras etapas técnicas serão tratadas em uma tela própria. */}
                      {(projeto?.etapa_tecnica === 'Planejamento'
                       || projeto?.etapa_tecnica === 'Execução') && (
                        <button
                          type="button"
                          onClick={() => setAlterarEtapaAberto(true)}
                          title="Alterar etapa técnica"
                          style={{
                            height: '34px', padding: '0 12px',
                            fontSize: '12px', fontWeight: '600',
                            border: `1px solid ${COR.azul}`, borderRadius: '5px',
                            backgroundColor: COR.branco, color: COR.azul,
                            cursor: 'pointer', whiteSpace: 'nowrap',
                          }}
                        >🔄 Alterar</button>
                      )}
                    </div>
                  </Campo>
                </div>

                {/* Linha 2: Cliente (flex) + Tipo */}
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: '1fr 200px',
                  gap: '12px',
                }}>
                  <Campo label="Cliente">
                    <input
                      value={projeto?.cliente_razao_social || projeto?.cliente_nome || '—'}
                      disabled style={inputDisabled} />
                  </Campo>
                  <Campo label="Tipo">
                    <input
                      value={TIPOS_LABEL[projeto?.tipo] || projeto?.tipo || '—'}
                      disabled style={inputDisabled} />
                  </Campo>
                </div>
              </Secao>

              {/* ═══════ Sessão 2 — Horas de Execução ═══════ */}
              <Secao titulo="Horas de Execução" icone="⏱️"
                aberta={secoes.horas}
                onToggle={() => toggleSec('horas')}>
                {itens.length === 0 ? (
                  <div style={{
                    padding: '20px', backgroundColor: COR.cinzaBg,
                    border: `1px dashed ${COR.borda}`,
                    borderRadius: '6px', color: COR.muted, fontSize: '13px',
                    textAlign: 'center',
                  }}>
                    Este projeto ainda não tem quantificação cadastrada.
                  </div>
                ) : (
                  <>
                    {/* Grid de itens */}
                    <div style={{
                      border: `1px solid ${COR.borda}`, borderRadius: '6px',
                      overflow: 'hidden', backgroundColor: COR.branco,
                    }}>
                      <div style={{ overflowX: 'auto' }}>
                        <table style={{
                          width: '100%', borderCollapse: 'collapse', fontSize: '12px',
                        }}>
                          <thead>
                            <tr style={{ backgroundColor: COR.cinzaBg, borderBottom: `1px solid ${COR.borda}` }}>
                              {['Tópico', 'Subtópico', 'Tipo', 'Quantif.', 'Save', 'Execução'].map((h, i) => (
                                <th key={i} style={{
                                  padding: '10px 12px',
                                  textAlign: i >= 3 ? 'right' : 'left',
                                  color: COR.azul, fontWeight: '700',
                                  fontSize: '10px', textTransform: 'uppercase', letterSpacing: '0.5px',
                                  whiteSpace: 'nowrap',
                                }}>{h}</th>
                              ))}
                            </tr>
                          </thead>
                          <tbody>
                            {itens.map(item => {
                              const est = estadoSave[item.id] || { valor: '0,00', erro: '' };
                              const original = parseFloat(item._horas_save_original) || 0;
                              const atual    = parseBR(est.valor);
                              const alterado = Math.abs(atual - original) > 0.001 && !est.erro;
                              return (
                                <tr key={item.id} style={{ borderBottom: `1px solid ${COR.borda}` }}>
                                  <td style={{ padding: '8px 12px', color: COR.muted, whiteSpace: 'nowrap' }}>
                                    {item.subtopico || item.topico}
                                  </td>
                                  <td style={{ padding: '8px 12px', color: COR.texto }}>
                                    <div>{item.subtopico_descricao}</div>
                                    {atribuicoesPorItem[item.id] && (() => {
                                      const cor = corDoAnalista(atribuicoesPorItem[item.id].analistaNome);
                                      return (
                                        <div style={{ marginTop: '3px' }}>
                                          <span style={{
                                            display: 'inline-block',
                                            padding: '1px 8px',
                                            borderRadius: '8px',
                                            fontSize: '10px', fontWeight: '600',
                                            backgroundColor: cor.bg,
                                            color: cor.cor,
                                            border: `1px solid ${cor.cor}55`,
                                          }} title="Analista Técnico atribuído">
                                            👤 {atribuicoesPorItem[item.id].analistaNome}
                                          </span>
                                        </div>
                                      );
                                    })()}
                                  </td>
                                  <td style={{ padding: '8px 12px' }}>
                                    {item.tipo_descricao ? (
                                      <span style={{
                                        display: 'inline-block', padding: '2px 8px',
                                        borderRadius: '10px', fontSize: '10px', fontWeight: '600',
                                        backgroundColor: (item.tipo_cor || '#888') + '22',
                                        color: item.tipo_cor || '#888',
                                      }}>{item.tipo_sigla || item.tipo_descricao}</span>
                                    ) : <span style={{ color: COR.muted }}>—</span>}
                                  </td>
                                  <td style={{ padding: '8px 12px', textAlign: 'right',
                                    fontWeight: '600', color: COR.texto, whiteSpace: 'nowrap' }}>
                                    {formatHoras(item.horas)}
                                  </td>
                                  <td style={{ padding: '6px 12px', textAlign: 'right' }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '4px', justifyContent: 'flex-end' }}>
                                      <input
                                        type="text"
                                        value={est.valor}
                                        onChange={e => handleSaveChange(item, e.target.value)}
                                        style={{
                                          width: '76px', height: '28px',
                                          padding: '0 8px', textAlign: 'right',
                                          border: `1px solid ${est.erro ? COR.vermelho : (alterado ? COR.amarelo : COR.borda)}`,
                                          borderRadius: '4px',
                                          fontSize: '12px', boxSizing: 'border-box',
                                          backgroundColor: est.erro ? COR.vermBg : (alterado ? COR.amlBg : COR.branco),
                                          outline: 'none',
                                        }}
                                        title={est.erro || (alterado ? 'Alteração não salva' : 'Edite o valor de save')}
                                      />
                                      {alterado && !est.erro && (
                                        <span title="Alteração não salva" style={{
                                          fontSize: '11px', color: COR.amarelo, fontWeight: '700',
                                        }}>●</span>
                                      )}
                                    </div>
                                    {est.erro && (
                                      <div style={{
                                        fontSize: '10px', color: COR.vermelho,
                                        marginTop: '2px', textAlign: 'right',
                                      }}>{est.erro}</div>
                                    )}
                                  </td>
                                  <td style={{ padding: '8px 12px', textAlign: 'right',
                                    fontWeight: '600', color: COR.verde, whiteSpace: 'nowrap' }}>
                                    {formatHoras(item.horas_execucao)}
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    </div>

                    {/* ═ Cards de totais por tipo + total geral ═ */}
                    <div style={{
                      marginTop: '14px',
                      display: 'flex', gap: '10px', flexWrap: 'wrap',
                    }}>
                      {totaisPorTipo.map((t, i) => (
                        <CardTotal key={t.tipo_atividade_id || i}
                          titulo={t.tipo_descricao}
                          cor={t.tipo_cor}
                          quantif={t.horas_quantificadas}
                          save={t.horas_save}
                          execucao={t.horas_execucao}
                        />
                      ))}
                      {/* Total geral em destaque */}
                      <CardTotal
                        titulo="Total Geral"
                        cor={COR.azul}
                        destaque
                        quantif={totalGeral.horas_quantificadas}
                        save={totalGeral.horas_save}
                        execucao={totalGeral.horas_execucao}
                      />
                    </div>
                  </>
                )}
              </Secao>

              {/* ═══════ Sessão 3 — Analistas Técnicos ═══════ */}
              <Secao titulo="Analistas Técnicos" icone="👥"
                aberta={secoes.analistas}
                onToggle={() => toggleSec('analistas')}>
                {/* Form de inclusão */}
                <div style={{
                  display: 'flex', gap: '8px', alignItems: 'flex-end',
                  marginBottom: '12px', flexWrap: 'wrap',
                }}>
                  <div style={{ flex: '1 1 280px', minWidth: '240px' }}>
                    <label style={{
                      display: 'block', fontSize: '11px', fontWeight: '600',
                      color: COR.muted, marginBottom: '4px',
                    }}>Adicionar Analista</label>
                    <select
                      value={colaboradorEscolhido}
                      onChange={e => setColaboradorEscolhido(e.target.value)}
                      style={{ ...inputStyle, cursor: 'pointer' }}
                    >
                      <option value="">— Selecione um colaborador —</option>
                      {colaboradores
                        .filter(c => !analistas.some(a => a.colaborador_id === c.id))
                        .map(c => (
                          <option key={c.id} value={c.id}>
                            {c.usuario_nome || c.nome}
                          </option>
                        ))}
                    </select>
                  </div>
                  <button
                    type="button"
                    onClick={adicionarAnalista}
                    disabled={!colaboradorEscolhido}
                    style={{
                      height: '34px', padding: '0 18px',
                      fontSize: '13px', fontWeight: '600',
                      border: 'none', borderRadius: '5px',
                      backgroundColor: colaboradorEscolhido ? COR.roxo : '#cccccc',
                      color: COR.branco,
                      cursor: colaboradorEscolhido ? 'pointer' : 'not-allowed',
                    }}
                  >+ Adicionar</button>
                </div>

                {/* Lista de analistas */}
                {carregandoAnalistas ? (
                  <div style={{
                    padding: '20px', textAlign: 'center', color: COR.muted, fontSize: '12px',
                  }}>Carregando analistas...</div>
                ) : analistas.length === 0 ? (
                  <div style={{
                    padding: '20px', backgroundColor: COR.cinzaBg,
                    border: `1px dashed ${COR.borda}`,
                    borderRadius: '6px', color: COR.muted, fontSize: '13px',
                    textAlign: 'center',
                  }}>
                    Nenhum analista atribuído ainda. Selecione um colaborador acima pra começar.
                  </div>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                    {analistas.map(a => {
                      const totalHoras = (a.itens || []).reduce(
                        (s, it) => s + (parseFloat(it.horas) || 0), 0);
                      return (
                        <div key={a.id} style={{
                          border: `1px solid ${COR.borda}`, borderRadius: '6px',
                          padding: '10px 14px', backgroundColor: COR.branco,
                        }}>
                          <div style={{
                            display: 'flex', alignItems: 'center', gap: '10px',
                            marginBottom: (a.itens || []).length > 0 ? '8px' : '0',
                            flexWrap: 'wrap',
                          }}>
                            {(() => {
                              const corA = corDoAnalista(a.nome_amigavel || a.nome);
                              return (
                                <>
                                  {/* Tag Analista Técnico — cor por hash do nome */}
                                  <span style={{
                                    display: 'inline-block', padding: '3px 10px',
                                    borderRadius: '12px', fontSize: '10px', fontWeight: '700',
                                    backgroundColor: corA.bg, color: corA.cor,
                                    border: `1px solid ${corA.cor}55`,
                                    textTransform: 'uppercase', letterSpacing: '0.5px',
                                  }}>Analista Técnico</span>
                                  {/* Nome */}
                                  <span style={{ fontSize: '14px', fontWeight: '600', color: corA.cor }}>
                                    {a.nome_amigavel || a.nome}
                                  </span>
                                </>
                              );
                            })()}
                            <span style={{ fontSize: '11px', color: COR.muted, marginLeft: 'auto' }}>
                              {(a.itens || []).length} item(ns) · {formatHoras(totalHoras)}h
                            </span>
                            <button
                              type="button"
                              onClick={() => editarItensAnalista(a)}
                              title="Editar itens atribuídos"
                              style={{
                                padding: '5px 10px', fontSize: '11px', fontWeight: '600',
                                border: `1px solid ${COR.azul}`, borderRadius: '4px',
                                backgroundColor: COR.branco, color: COR.azul,
                                cursor: 'pointer',
                              }}
                            >📌 Itens</button>
                            <button
                              type="button"
                              onClick={() => removerAnalista(a)}
                              title="Remover analista"
                              style={{
                                padding: '5px 10px', fontSize: '11px', fontWeight: '600',
                                border: `1px solid ${COR.vermelho}`, borderRadius: '4px',
                                backgroundColor: COR.branco, color: COR.vermelho,
                                cursor: 'pointer',
                              }}
                            >🗑</button>
                          </div>

                          {/* Itens atribuídos (pílulas) + botão DOS016 na mesma linha, abaixo da tracejada */}
                          {(a.itens || []).length > 0 && (
                            <div style={{
                              padding: '6px 0 0 0',
                              borderTop: `1px dashed ${COR.borda}`,
                              display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '4px',
                            }}>
                              {a.itens.map(it => (
                                <span key={it.atribuicao_id} style={{
                                  fontSize: '10px',
                                  backgroundColor: COR.cinzaBg,
                                  padding: '2px 8px', borderRadius: '10px',
                                  color: COR.muted,
                                  border: `1px solid ${COR.borda}`,
                                }}>
                                  {it.subtopico}: {formatHoras(it.horas)}h
                                </span>
                              ))}
                              <button
                                type="button"
                                onClick={() => gerarDOS016(a)}
                                title="Gerar Documento de Horas de Execução (DOS016)"
                                style={{
                                  marginLeft: 'auto',
                                  padding: '4px 10px', fontSize: '11px', fontWeight: '700',
                                  border: `1px solid ${COR.verde}`, borderRadius: '4px',
                                  backgroundColor: COR.verde, color: COR.branco,
                                  cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: '5px',
                                }}
                              >
                                <svg width="13" height="13" viewBox="0 0 24 24" fill="none"
                                     stroke="currentColor" strokeWidth="2"
                                     strokeLinecap="round" strokeLinejoin="round">
                                  <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                                  <polyline points="14 2 14 8 20 8" />
                                  <line x1="8" y1="13" x2="16" y2="13" />
                                  <line x1="8" y1="17" x2="16" y2="17" />
                                </svg>
                                DOS016
                              </button>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </Secao>

              {/* ═══════ Sessão 4 — Capacidade (alocação de horas) ═══════ */}
              <Secao titulo="Capacidade" icone="📊"
                aberta={secoes.capacidade}
                onToggle={() => toggleSec('capacidade')}>
                <SessaoCapacidade
                  negocio={projeto}
                  usuario={usuario}
                  refreshKey={capacidadeRefreshKey}
                />
              </Secao>

              {/* ═══════ Sessão 5 — Timeline Técnica ═══════ */}
              <Secao titulo="Timeline" icone="🕒"
                aberta={secoes.timeline}
                onToggle={() => toggleSec('timeline')}>
                {/* Botão único "Eventos" abre o modal com a lista (calls + anotações) */}
                <div style={{
                  display: 'flex', gap: '8px',
                  marginBottom: '14px', flexWrap: 'wrap',
                }}>
                  <button
                    type="button"
                    onClick={() => setCallsModalAberto(true)}
                    style={{
                      padding: '8px 16px', fontSize: '13px', fontWeight: '600',
                      border: 'none', borderRadius: '5px',
                      backgroundColor: COR.azul, color: COR.branco,
                      cursor: 'pointer',
                      display: 'flex', alignItems: 'center', gap: '6px',
                    }}
                    title="Ver e criar eventos do projeto (calls, anotações...)"
                  >📅 Eventos</button>
                </div>

                {/* Timeline visual */}
                {carregandoTL ? (
                  <div style={{
                    padding: '20px', textAlign: 'center',
                    color: COR.muted, fontSize: '12px',
                  }}>Carregando timeline...</div>
                ) : (
                  <TimelineTecnica
                    eventos={eventosTimeline}
                    negocio={projeto}
                    onEventoClick={async (ev) => {
                      // Eventos com referência a anexo: 'DOS010 Vinculado'
                      // (criado quando avança pra F com etapa técnica = Planejamento)
                      // ou 'Anexo' (uploads diretos na fase técnica). Em ambos
                      // os casos, referencia_id aponta pra negocios_anexos.id.
                      const ehAnexo = (ev.tipo_evento === 'DOS010 Vinculado' ||
                                       ev.tipo_evento === 'Anexo');
                      if (ehAnexo && ev.referencia_id && projeto?.id) {
                        try {
                          // Resolve nome original via lista de anexos do projeto
                          const lista = await negocioAnexoService.listar(projeto.id);
                          const anexo = lista.find(a => a.id === ev.referencia_id);
                          if (!anexo) {
                            alert('Este anexo foi excluído ou não está mais disponível.');
                            return;
                          }
                          await negocioAnexoService.download(projeto.id, anexo.id, anexo.nome_original);
                        } catch (err) {
                          console.error('Erro ao baixar anexo da timeline técnica:', err);
                          alert('Não foi possível baixar o arquivo.');
                        }
                      }
                    }}
                  />
                )}
              </Secao>
            </>
          )}
        </div>

        {/* Footer */}
        <div style={{
          padding: '12px 20px', borderTop: `1px solid ${COR.borda}`,
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          gap: '12px', flexWrap: 'wrap',
          backgroundColor: COR.cinzaBg,
        }}>
          {/* Lado esquerdo — mensagens */}
          <div style={{ flex: 1, minWidth: '200px' }}>
            {erro && (
              <span style={{
                fontSize: '12px', color: COR.vermelho, fontWeight: '600',
              }}>⚠️ {erro}</span>
            )}
            {!erro && msgSucesso && (
              <span style={{
                fontSize: '12px', color: COR.verde, fontWeight: '600',
              }}>{msgSucesso}</span>
            )}
            {!erro && !msgSucesso && temPendencias && (
              <span style={{
                fontSize: '12px', color: COR.amarelo, fontWeight: '600',
              }}>● Você tem alterações não salvas</span>
            )}
            {!erro && !msgSucesso && !temPendencias && (
              <span style={{ fontSize: '11px', color: COR.muted }}>
                Edite os valores de Save e clique em "Salvar".
              </span>
            )}
          </div>
          {/* Lado direito — botões */}
          <div style={{ display: 'flex', gap: '8px' }}>
            <button onClick={handleFechar} style={{
              padding: '8px 18px', fontSize: '13px', fontWeight: '600',
              border: `1px solid ${COR.borda}`, borderRadius: '5px',
              backgroundColor: COR.branco, color: COR.texto, cursor: 'pointer',
            }}>Fechar</button>
            <button onClick={salvarTudo} disabled={salvando || !temPendencias}
              style={{
                padding: '8px 22px', fontSize: '13px', fontWeight: '600',
                border: 'none', borderRadius: '5px',
                backgroundColor: (salvando || !temPendencias) ? '#cccccc' : COR.verde,
                color: COR.branco,
                cursor: (salvando || !temPendencias) ? 'not-allowed' : 'pointer',
                opacity: salvando ? 0.7 : 1,
              }}>
              {salvando ? 'Salvando...' : '💾 Salvar'}
            </button>
          </div>
        </div>
      </div>

      {/* Sub-modal pra selecionar itens do analista */}
      <SelecionarItensModal
        aberto={itensModalAberto}
        itensQuantif={dados?.itens || []}
        itensSelecionados={
          (analistaEdicao?.itens || []).map(i => i.item_quantificacao_id)
        }
        atribuicoesPorItem={atribuicoesPorItem}
        analistaAtualId={analistaEdicao?.id || null}
        onCancelar={() => {
          setItensModalAberto(false);
          setAnalistaEdicao(null);
        }}
        onConfirmar={handleConfirmarItens}
      />

      {/* Modal de Eventos (calls + anotações) — reusa o CallsModal passando
          fase='T'. O CallsModal embute internamente o AnotacaoModal. */}
      {projeto && callsModalAberto && (
        <CallsModal
          aberto={callsModalAberto}
          negocio={projeto}
          clienteId={projeto.cliente_id}
          usuarioId={usuario?.id || null}
          fase="T"
          onFechar={handleFecharCallsModal}
          onCallCriada={handleCallCriada}
        />
      )}

      {/* Modal de Alterar Etapa Técnica (reusa AlterarEtapaModal modoTecnico) */}
      {projeto && (
        <AlterarEtapaModal
          aberto={alterarEtapaAberto}
          negocio={projeto}
          usuarioId={usuario?.id || null}
          modoTecnico={true}
          onFechar={() => setAlterarEtapaAberto(false)}
          onSucesso={handleSucessoAlterarEtapa}
        />
      )}
    </>
  );
}

// ─── Componente Seção (mesmo padrão do CRM Negócios) ──────────────────────
// Card retangular com cabeçalho clicável que expande/colapsa o conteúdo.
function Secao({ icone, titulo, aberta, onToggle, children }) {
  return (
    <div style={{
      border: `1px solid ${COR.borda}`, borderRadius: '6px',
      marginBottom: '10px', overflow: 'hidden', backgroundColor: COR.branco,
    }}>
      <div onClick={onToggle} style={{
        padding: '10px 14px',
        backgroundColor: aberta ? COR.azulBg : COR.cinzaBg,
        borderBottom: aberta ? `1px solid ${COR.borda}` : 'none',
        cursor: 'pointer', userSelect: 'none',
        display: 'flex', alignItems: 'center', gap: '10px',
        transition: 'background-color 0.15s',
      }}>
        <span style={{ fontSize: '14px' }}>{icone}</span>
        <span style={{
          flex: 1, fontSize: '14px', fontWeight: '700',
          fontFamily: "'Manrope', sans-serif",
          color: aberta ? COR.azul : COR.texto,
          textTransform: 'uppercase', letterSpacing: '0.5px',
        }}>{titulo}</span>
        <span style={{ fontSize: '12px', color: COR.muted }}>
          {aberta ? '▲' : '▼'}
        </span>
      </div>
      {aberta && <div style={{ padding: '14px' }}>{children}</div>}
    </div>
  );
}

function Campo({ label, children }) {
  return (
    <div>
      <label style={{
        display: 'block', fontSize: '11px', fontWeight: '600',
        color: COR.muted, marginBottom: '4px',
      }}>{label}</label>
      {children}
    </div>
  );
}

function CardTotal({ titulo, cor, destaque, quantif, save, execucao }) {
  const pct = pctSave(save, quantif);
  return (
    <div style={{
      flex: '1 1 180px', minWidth: '180px',
      border: `1px solid ${destaque ? COR.azul : COR.borda}`,
      borderRadius: '6px',
      backgroundColor: destaque ? COR.azulBg : COR.branco,
      padding: '10px 12px',
    }}>
      <div style={{
        fontSize: '11px', fontWeight: '700',
        color: cor,
        textTransform: 'uppercase', letterSpacing: '0.5px',
        marginBottom: '8px',
        display: 'flex', alignItems: 'center', gap: '6px',
      }}>
        <span style={{
          display: 'inline-block', width: '8px', height: '8px',
          borderRadius: '50%', backgroundColor: cor,
        }}></span>
        {titulo}
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '6px 10px', fontSize: '11px' }}>
        <div style={{ color: COR.muted }}>Quantif.</div>
        <div style={{ textAlign: 'right', fontWeight: '600', color: COR.texto }}>{formatHoras(quantif)}h</div>
        <div style={{ color: COR.muted }}>Save</div>
        <div style={{ textAlign: 'right', fontWeight: '600', color: COR.amarelo }}>{formatHoras(save)}h</div>
        <div style={{ color: COR.muted }}>% Save</div>
        <div style={{ textAlign: 'right', fontWeight: '600', color: COR.amarelo }}>{pct}</div>
        <div style={{ color: COR.muted, paddingTop: '4px', borderTop: `1px solid ${COR.borda}` }}>Execução</div>
        <div style={{
          textAlign: 'right', fontWeight: '700', color: COR.verde,
          paddingTop: '4px', borderTop: `1px solid ${COR.borda}`,
        }}>{formatHoras(execucao)}h</div>
      </div>
    </div>
  );
}

export default PlanejamentoModal;
